import { StyledComponent } from "@emotion/styled";
import { useForkRef } from "@smartsheet/lodestar-utils";
import React, { forwardRef, Ref, useMemo, DetailedHTMLProps, HTMLAttributes } from "react";
import { useOverflowTooltip } from "../../../util/useOverflowTooltip";
import { Tooltip, TooltipBody } from "../../tooltip";
import { TypeRampComponentBaseProps } from "../styled/TypeRampComponentBase";
import { getTypeRampV2PrecomputedComponent, SystemAndVariant } from "../styled/TypeRampV2PrecomputedComponents";
import { TypeRampV2Props } from "../types";

const elementIsScrollable = (elem: HTMLElement | null) => {
    return !!elem && (elem.offsetHeight < elem.scrollHeight || elem.offsetWidth < elem.scrollWidth);
};

export const TypeRampV2 = forwardRef((props: TypeRampV2Props, parentRef: Ref<HTMLElement>): JSX.Element => {
    const { renderAs, children, clientId, omitTooltip, ...rest } = props;

    const element = renderAs ? renderAs : "span";

    // Memoizing this to ensure we won't create a new component each time.
    // withComponent returns a new component.
    // do we need the theme in here too??
    const ComponentRoot = useMemo(() => {
        const Component = getTypeRampV2PrecomputedComponent({
            system: props.system,
            variant: props.variant,
            // This assertion is generally ok since the props interface will ensure these match.
        } as SystemAndVariant);
        return Component.withComponent(element);
    }, [element, props.variant, props.system]) as StyledComponent<
        DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>,
        TypeRampComponentBaseProps,
        object
    >;

    const { showOverflowTooltip, tooltipProps, targetProps, elementToMeasureRef } = useOverflowTooltip({
        tooltipTargetRef: parentRef,
        elementIsOverflowing: elementIsScrollable,
        isDisabled: omitTooltip,
    });

    const combinedRef = useForkRef(elementToMeasureRef, targetProps.ref);

    return (
        <ComponentRoot data-client-id={clientId} {...rest} {...(omitTooltip ? {} : targetProps)} ref={combinedRef}>
            {children}
            {showOverflowTooltip && (
                <Tooltip {...tooltipProps}>
                    <TooltipBody>{children}</TooltipBody>
                </Tooltip>
            )}
        </ComponentRoot>
    );
});

TypeRampV2.displayName = "TypeRampV2";
